"use client";
import React, { useRef, useEffect, useState, RefObject } from "react";
import styles from "@@/styles/components/shared/Slider.module.scss";
import { exo2Bold } from "@@/utils/fonts";
import useAppearOnScroll from "@@/hooks/useAppearsOnScroll";
import ProgressiveImage from "@@/utils/ProgressiveImage";
import { StaticImageData } from "next/image";

interface CardItem {
    title: string;
    image: StaticImageData;
    image4k: StaticImageData;
    link: string;
}

interface StickyHorizontalSliderProps {
    title: string;
    items: CardItem[];
    sectionId?: string;
    className?: string;
    btns?: boolean;
}

export default function Slider({ title, items, sectionId, className, btns }: StickyHorizontalSliderProps) {
    const sectionRef = useRef<HTMLElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const textContainerRef = useRef<HTMLDivElement>(null);

    const [activeIndex, setActiveIndex] = useState(0);
    const [textZoneOpacity, setTextZoneOpacity] = useState(1);

    const titleAnim = useAppearOnScroll<HTMLHeadingElement>({ from: "fromBottom", duration: 800, delay: 0 });
    const textAnim = useAppearOnScroll<HTMLUListElement>({ from: "fromBottom", duration: 800, delay: 0 });

    const isDraggingRef = useRef(false);
    const startXRef = useRef(0);
    const currentTranslateRef = useRef(0);
    const prevTranslateRef = useRef(0);
    const animationIdRef = useRef<number | null>(null);
    const movedRef = useRef(false);

    const activeIndexRef = useRef(activeIndex);
    useEffect(() => { activeIndexRef.current = activeIndex; }, [activeIndex]);

    const SWIPE_THRESHOLD_MIN_PX = 30;
    const SWIPE_THRESHOLD_RATIO = 0.15;

    const widthRef = useRef(0);
    const stepPxRef = useRef(0);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        // init
        widthRef.current = el.clientWidth;
        stepPxRef.current = widthRef.current / Math.max(items.length, 1);

        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            const w =
                (Array.isArray(entry.contentBoxSize)
                    ? entry.contentBoxSize[0]?.inlineSize
                    : (entry as any).contentBoxSize?.inlineSize) || el.clientWidth;
            widthRef.current = w;
            stepPxRef.current = w / Math.max(items.length, 1);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [items.length]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const stepPx = stepPxRef.current || (el.clientWidth / Math.max(items.length, 1));
        const targetTranslate = -activeIndex * stepPx;

        el.style.transition = "transform 0.5s ease";
        const id = requestAnimationFrame(() => {
            const percent = widthRef.current ? (targetTranslate / widthRef.current) * 100 : 0;
            el.style.transform = `translateX(${percent}%)`;
        });

        prevTranslateRef.current = targetTranslate;
        currentTranslateRef.current = targetTranslate;

        setTextZoneOpacity(activeIndex === 0 ? 1 : 0);
        return () => cancelAnimationFrame(id);
    }, [activeIndex, items.length]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const getX = (e: TouchEvent | MouseEvent) =>
            e instanceof TouchEvent ? e.touches[0].clientX : (e as MouseEvent).clientX;

        const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

        const animation = () => {
            const percent = widthRef.current
                ? (currentTranslateRef.current / widthRef.current) * 100
                : 0;
            container.style.transform = `translateX(${percent}%)`;
            if (isDraggingRef.current) {
                animationIdRef.current = requestAnimationFrame(animation);
            }
        };

        const onStart = (event: TouchEvent | MouseEvent) => {
            isDraggingRef.current = true;
            movedRef.current = false;
            startXRef.current = getX(event);

            currentTranslateRef.current = prevTranslateRef.current;

            container.style.transition = "none";
            animationIdRef.current = requestAnimationFrame(animation);
        };

        const onMove = (event: TouchEvent | MouseEvent) => {
            if (!isDraggingRef.current) return;
            container.classList.add(styles.dragging);

            const currentX = getX(event);
            const delta = currentX - startXRef.current;
            if (Math.abs(delta) > 3) movedRef.current = true;

            const totalItems = Math.max(items.length, 1);
            const stepPx = stepPxRef.current;

            const minTranslate = -stepPx * (totalItems - 1);
            const maxTranslate = 0;

            currentTranslateRef.current = clamp(prevTranslateRef.current + delta, minTranslate, maxTranslate);

            const virtualIndex = stepPx ? (-currentTranslateRef.current) / stepPx : 0;
            const progress01 = clamp(virtualIndex, 0, 1);
            setTextZoneOpacity(1 - progress01);
        };

        const onEnd = () => {
            if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
            isDraggingRef.current = false;
            container.classList.remove(styles.dragging);

            const totalItems = Math.max(items.length, 1);
            const stepPx = stepPxRef.current;
            const thresholdPx = Math.max(SWIPE_THRESHOLD_MIN_PX, stepPx * SWIPE_THRESHOLD_RATIO);

            const movedBy = currentTranslateRef.current - prevTranslateRef.current;
            const currIndex = activeIndexRef.current;
            let newIndex = currIndex;

            const absMoved = Math.abs(movedBy);
            const allowMulti = typeof window !== "undefined" && stepPx > 0 && window.innerWidth > 2 * stepPx;

            if (absMoved >= thresholdPx) {
                let jump = 1;
                if (allowMulti) {
                    jump = Math.floor((absMoved + stepPx * 0.25) / stepPx);
                    if (jump < 1) jump = 1;
                }

                if (movedBy <= -thresholdPx) {
                    newIndex = Math.min(currIndex + jump, totalItems - 1);
                } else if (movedBy >= thresholdPx) {
                    newIndex = Math.max(currIndex - jump, 0);
                }
            } else {
                container.style.transition = "transform 0.25s ease";
                const backPercent = widthRef.current
                    ? (prevTranslateRef.current / widthRef.current) * 100
                    : 0;
                container.style.transform = `translateX(${backPercent}%)`;
                setTextZoneOpacity(currIndex === 0 ? 1 : 0);
            }

            if (newIndex !== currIndex) {
                setActiveIndex(newIndex);
            }
        };

        container.addEventListener("touchstart", onStart, { passive: true });
        container.addEventListener("touchmove", onMove, { passive: true });
        container.addEventListener("touchend", onEnd);

        container.addEventListener("mousedown", onStart);
        container.addEventListener("mousemove", onMove);
        container.addEventListener("mouseup", onEnd);
        container.addEventListener("mouseleave", () => isDraggingRef.current && onEnd());

        return () => {
            container.removeEventListener("touchstart", onStart);
            container.removeEventListener("touchmove", onMove);
            container.removeEventListener("touchend", onEnd);

            container.removeEventListener("mousedown", onStart);
            container.removeEventListener("mousemove", onMove);
            container.removeEventListener("mouseup", onEnd);
            container.removeEventListener("mouseleave", () => isDraggingRef.current && onEnd());
        };
    }, [items.length]);

    const handleNext = () => setActiveIndex((prev) => (prev < items.length - 1 ? prev + 1 : prev));
    const handlePrev = () => setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));

    return (
        <section id={sectionId} className={`px-11 ${styles.section} ${className} overflow-x-hidden`} ref={sectionRef}>
            <div className={`p-5 ${styles.sliderContainer}`}>
                <div className={styles.textZone} ref={textContainerRef} style={{ opacity: textZoneOpacity }}>
                    <h2
                        ref={titleAnim.ref}
                        className={`${exo2Bold.className} ${titleAnim.className}`}
                        style={titleAnim.style}
                        dangerouslySetInnerHTML={{ __html: title }}
                    />
                    <ul
                        ref={textAnim.ref as RefObject<HTMLUListElement>}
                        className={`${textAnim.className} ${styles.sliderList}`}
                        style={textAnim.style}
                    >
                        {items.map((item, index) => (
                            <li key={index}>{item.title}</li>
                        ))}
                    </ul>
                </div>

                {btns && (
                    <div className={styles.sliderBtns}>
                        <button onClick={handlePrev} disabled={activeIndex === 0} className={`${styles.btnLeft} caroussel-btn fs-1_5`}>←</button>
                        <button onClick={handleNext} disabled={activeIndex === items.length - 1} className={`${styles.btnRight} caroussel-btn fs-1_5`}>→</button>
                    </div>
                )}

                <div className={styles.cardsContainer} ref={containerRef}>
                    {items.map((item, index) => (
                        <a
                            href={item.link}
                            key={index}
                            onDragStart={(e) => e.preventDefault()}
                            onClick={(e) => {
                                if (movedRef.current) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                            }}
                            className={styles.card}
                            rel="noopener noreferrer"
                        >
                            <ProgressiveImage
                                lowSrc={item.image}
                                highSrc={item.image4k}
                                alt={item.title}
                                classLowRes={`df`}
                                width={item.image4k.width}
                                height={item.image4k.height}
                                quality={58}
                                className={`rounded-2 overflow-hidden h-100-with-img w-100 h-100 position-relative mb-1`}
                            />
                            <h3>{item.title}</h3>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}
